import React, { useState } from 'react';
import { User, Phone, Lock, KeyRound, Loader2 } from 'lucide-react';
import AuthField from './AuthField';
import ImageCaptchaField from './ImageCaptchaField';
import SliderCaptcha from './SliderCaptcha';
import TurnstileWidget from './TurnstileWidget';
import Toast from './Toast';
import { useTranslation } from '../i18n/LocaleContext';

const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '';

const Register = ({ onRegister, onSwitchToLogin }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    phone: '',
    password: '',
    confirm_password: '',
    referral_code: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaRefresh, setCaptchaRefresh] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  // token 一次性:注册失败后自增 key 重挂挂件,让用户重新验证
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAttempted(true);

    if (!formData.username || !formData.phone || !formData.password || !formData.referral_code) {
      setError(t('fillAllFields'));
      return;
    }
    if (formData.password !== formData.confirm_password) {
      setError(t('passwordMismatch'));
      return;
    }
    if (formData.password.length < 6) {
      setError(t('passwordTooShort'));
      return;
    }
    if (!captchaCode) {
      setError(t('imageCaptchaRequired'));
      setCaptchaRefresh((k) => k + 1);
      return;
    }
    if (TURNSTILE_SITEKEY ? !turnstileToken : !captchaOk) {
      setError(t('captchaRequired'));
      return;
    }

    setLoading(true);
    try {
      await onRegister({ ...formData, captchaId, captchaCode, turnstileToken });
      setToast({ message: t('registrationSuccess'), type: 'success' });
    } catch (err) {
      setError(err.message || t('registrationFailed'));
      setToast({ message: err.message || t('registrationFailed'), type: 'error' });
      setCaptchaRefresh((k) => k + 1);
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <p className="rounded-xl bg-red-50 px-3.5 py-2.5 text-[12.5px] font-medium text-red-600">{error}</p>
        )}

        {/* Referral first: it is the funnel, burying it costs conversions. */}
        <div>
          <div className="relative">
            <AuthField
              pill
              icon={KeyRound}
              type="text"
              name="referral_code"
              value={formData.referral_code}
              onChange={handleChange}
              required
              label={t('enterReferralCode')}
              placeholder="••••••"
              error={attempted && !formData.referral_code}
            />
          </div>
          {!formData.referral_code && (
            <p className="mt-1.5 text-[11.5px] font-medium text-ink-muted">{t('referralRequired')}</p>
          )}
        </div>

        <AuthField
          pill
          icon={User}
          type="text"
          name="username"
          value={formData.username}
          onChange={handleChange}
          required
          autoComplete="username"
          label={t('username')}
        />

        <AuthField
          pill
          icon={Phone}
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          autoComplete="tel"
          label={t('phone')}
        />

        <div className="grid grid-cols-2 gap-2.5">
          <AuthField
            pill
            icon={Lock}
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete="new-password"
            label={t('password')}
            placeholder="••••••"
          />
          <AuthField
            pill
            icon={Lock}
            type="password"
            name="confirm_password"
            value={formData.confirm_password}
            onChange={handleChange}
            required
            autoComplete="new-password"
            label={t('confirmRegistrationPassword')}
            placeholder="••••••"
          />
        </div>

        <ImageCaptchaField
          refreshSignal={captchaRefresh}
          onChange={(code, id) => { setCaptchaCode(code); setCaptchaId(id); }}
        />

        {TURNSTILE_SITEKEY ? (
          <TurnstileWidget key={turnstileKey} onToken={setTurnstileToken} />
        ) : (
          <SliderCaptcha onVerified={setCaptchaOk} />
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full h-[52px] mt-1 flex items-center justify-center gap-2 rounded-xl
            bg-brand-500
            text-[15px] font-bold text-white disabled:opacity-55"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {loading ? t('processing') : t('registrationConfirm')}
        </button>

        <p className="pt-1 text-center text-[12.5px] text-ink-muted">
          {t('haveAccount')}{' '}
          <button type="button" onClick={onSwitchToLogin} className="font-bold text-brand-600">
            {t('login')}
          </button>
        </p>
      </form>
    </>
  );
};

export default Register;
